import { forwardRef, useMemo } from 'react';

import { Box, BoxProps, Flex, Popover, PopoverTrigger, rem, StyleProps } from '@bonusly/design-system';

import { ProfileLink } from '../../../profile/components/profile-link';
import { UserBonusFeedProps } from '../../types/user';
import { splitOverflow } from '../../utils/array';
import { pluck } from '../../utils/object';
import { UserAvatar } from './UserAvatar';
import { UserAvatarExcess } from './UserAvatarExcess';
import { UserPopover } from './UserPopover';

/**
 * A wrapper component that makes the avatar accessible as a popover trigger.
 * Uses a button element to properly support aria-expanded attribute.
 */
const AvatarTriggerWrapper = forwardRef<HTMLButtonElement, BoxProps & { children: React.ReactNode }>(({ children, ...rest }, ref) => (
	<Box ref={ref} as="button" type="button" display="inline-flex" alignItems="center" bg="transparent" border="none" p={0} cursor="pointer" {...rest}>
		{children}
	</Box>
));

AvatarTriggerWrapper.displayName = 'AvatarTriggerWrapper';

export const DEFAULT_MAX_USERS = 5;

interface UserAvatarGroupProps extends StyleProps {
	onAvatarClick?: VoidFunction;
	isPopoverDisabled?: boolean;
	linkToUsers?: boolean;
	max?: number;
	size?: 'lg' | 'xl';
	users: UserBonusFeedProps[];
	variant?: 'compact' | 'inline';
}

interface GenerateProps extends Pick<UserAvatarGroupProps, 'size' | 'users' | 'variant'> {
	i: number;
}

export const sizeMap = {
	'2xs': 24,
	xs: 32,
	sm: 32,
	md: 44,
	lg: 52,
	xl: 64,
	'2xl': 72,
};

const avatarProps = ({ i, size = 'lg', variant, users }: GenerateProps) => {
	if (variant === 'compact' && users.length > 1) {
		return {
			marginEnd: rem(-16),
			mt: rem(12 * i),
			size: 'sm' as any,
		};
	}

	return {
		marginEnd: rem((sizeMap[size] * -1) / 2.75),
		size,
	};
};

export const UserAvatarGroup: React.FC<UserAvatarGroupProps> = ({
	isPopoverDisabled,
	linkToUsers,
	max = DEFAULT_MAX_USERS,
	onAvatarClick,
	size,
	users,
	variant = 'inline',
	...rest
}) => {
	const isCompact = variant === 'compact';

	const { head, tail, doesOverflow, overflowCount } = splitOverflow(users, {
		max: isCompact ? 1 : max,
		minOverflow: 2,
	});

	const overflowUserNames = useMemo(() => tail.map(pluck('display_name')), [tail]);

	return (
		<Flex dir="row" {...rest}>
			{head.map((user, i) => {
				const avatar = (
					<UserAvatar
						boxShadow="0 0 0 2px var(--chakra-colors-bg-surface)"
						user={user}
						onClick={onAvatarClick}
						hideOverlay
						{...avatarProps({ i, size, users, variant })}
					/>
				);

				return (
					<Popover key={user.id} trigger="hover" isOpen={isPopoverDisabled ? false : undefined}>
						<PopoverTrigger>
							{linkToUsers ? (
								<ProfileLink userId={user.id}>{avatar}</ProfileLink>
							) : (
								<AvatarTriggerWrapper aria-label={user.display_name}>{avatar}</AvatarTriggerWrapper>
							)}
						</PopoverTrigger>
						<UserPopover user={user} />
					</Popover>
				);
			})}

			{doesOverflow && <UserAvatarExcess count={overflowCount} userNames={overflowUserNames} {...avatarProps({ i: 1, size, users, variant })} />}
		</Flex>
	);
};
